import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse, PaginatedResponse } from '../api-types';

// ─── Types ────────────────────────────────────────────────

export type ApplicationStatus =
  | 'APPLIED'
  | 'UNDER_REVIEW'
  | 'SHORTLISTED'
  | 'ASSESSMENT_SENT'
  | 'INTERVIEW_SCHEDULED'
  | 'INTERVIEW_COMPLETED'
  | 'OFFER_SENT'
  | 'OFFER_ACCEPTED'
  | 'OFFER_REJECTED'
  | 'SELECTED'
  | 'REJECTED'
  | 'WITHDRAWN';

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  APPLIED: 'Applied',
  UNDER_REVIEW: 'Under review',
  SHORTLISTED: 'Shortlisted',
  ASSESSMENT_SENT: 'Assessment sent',
  INTERVIEW_SCHEDULED: 'Interview scheduled',
  INTERVIEW_COMPLETED: 'Interviewed',
  OFFER_SENT: 'Offer sent',
  OFFER_ACCEPTED: 'Offer accepted',
  OFFER_REJECTED: 'Offer declined',
  SELECTED: 'Hired',
  REJECTED: 'Not selected',
  WITHDRAWN: 'Withdrawn',
};

export interface UpcomingInterview {
  id: string;
  type: InterviewType;
  scheduledAt: string;
  durationMins: number;
}

export type InterviewType = 'VIDEO' | 'PHONE' | 'IN_PERSON' | 'TECHNICAL' | 'PANEL';

export interface Application {
  id: string;
  userId: string;
  jobId: string;
  resumeId: string | null;
  resumeVersionId: string | null;
  coverLetterId: string | null;
  status: ApplicationStatus;
  source: string;
  customAnswers: CustomAnswer[] | null;
  attachedDocuments: AttachedDocument[] | null;
  isResumeShared: boolean;
  resumeSharedAt: string | null;
  coverNote: string | null;
  expectedSalary: number | null;
  createdAt: string;
  updatedAt: string;
  job?: {
    id: string;
    title: string;
    status: string;
    organization: {
      id: string;
      name: string;
      logoUrl: string | null;
    };
  };
  user?: {
    id: string;
    name: string;
    email: string | null;
    careerProfile?: {
      fullName: string | null;
      avatarUrl: string | null;
      headline: string | null;
    } | null;
  };
  currentStage?: {
    movedAt?: string;
    stage: {
      id: string;
      name: string;
      color: string | null;
      order: number;
    };
  } | null;
  upcomingInterviews?: UpcomingInterview[];
  _count?: { interviews: number };
}

/** The candidate's own view of an application (no hiring-team internals). */
export interface MyApplicationDetail {
  id: string;
  jobId: string;
  status: ApplicationStatus;
  coverNote: string | null;
  expectedSalary: number | null;
  customAnswers: CustomAnswer[] | null;
  attachedDocuments: AttachedDocument[] | null;
  isResumeShared: boolean;
  createdAt: string;
  job: {
    id: string;
    title: string;
    status: string;
    location: string | null;
    organization: { id: string; name: string; logoUrl: string | null };
  };
  resume: { id: string; title: string } | null;
  coverLetter: { id: string; title: string } | null;
  currentStage: { name: string; movedAt: string } | null;
  statusHistory: {
    id: string;
    fromStatus: ApplicationStatus | null;
    toStatus: ApplicationStatus;
    notes: string | null;
    byCandidate: boolean;
    createdAt: string;
  }[];
  interviews: {
    id: string;
    type: InterviewType;
    scheduledAt: string;
    durationMins: number;
    location: string | null;
    meetingLink: string | null;
    status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'RESCHEDULED' | 'NO_SHOW';
    cancelReason: string | null;
  }[];
  offers: {
    id: string;
    status: string;
    ctcLpa: number | null;
    joiningDate: string | null;
    expiresAt: string | null;
    createdAt: string;
  }[];
}

export interface CustomAnswer {
  questionId: string;
  question: string;
  answer: string;
}

export interface AttachedDocument {
  documentId: string;
  fileName: string;
  shareToken: string;
}

export interface SubmitApplicationRequest {
  resumeId?: string;
  coverLetterId?: string;
  coverNote?: string;
  expectedSalary?: number;
  customAnswers?: CustomAnswer[];
  /** The applicant's documents to share; share links are created on the server. */
  documentIds?: string[];
}

export interface ApplicationDraft {
  id: string;
  jobId: string;
  data: SubmitApplicationRequest;
  updatedAt: string;
}

export interface ApplicationListParams {
  page?: number;
  pageSize?: number;
  status?: ApplicationStatus;
  search?: string;
  sortBy?: 'NEWEST' | 'OLDEST' | 'STATUS';
}

export interface OrgApplicationParams {
  page?: number;
  pageSize?: number;
  jobId?: string;
  status?: ApplicationStatus;
  /** Leave out rejected and withdrawn applications. */
  activeOnly?: boolean;
  stageId?: string;
  search?: string;
  sortBy?: 'NEWEST' | 'OLDEST' | 'NAME_AZ' | 'STALEST';
}

export interface PipelineSummary {
  byStatus: Partial<Record<ApplicationStatus, number>>;
  total: number;
  newThisWeek: number;
  activeJobs: number;
  interviewsThisWeek: number;
  offers: { sent: number; accepted: number };
}

export interface SendOfferRequest {
  ctcLpa?: number;
  joiningDate?: string;
  expiresAt?: string;
  message?: string;
}

export interface BulkActionRequest {
  applicationIds: string[];
  action: 'MOVE_STAGE' | 'REJECT' | 'SEND_ASSESSMENT';
  stageId?: string;
  assessmentId?: string;
  rejectReason?: string;
  notifyCandidate?: boolean;
}

export interface BulkActionResult {
  success: number;
  failed: number;
  errors: { applicationId: string; message: string }[];
}

export interface ApplicationNote {
  id: string;
  content: string;
  isPrivate: boolean;
  createdAt: string;
  author?: { id: string; name: string };
}

// ─── Service ──────────────────────────────────────────────

export class ApplicationApiService {
  static async submitApplication(
    jobId: string,
    data: SubmitApplicationRequest
  ): Promise<Application> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<Application>>(
      API_ENDPOINTS.APPLICATIONS.APPLY(jobId),
      data
    );
    return response.data.data;
  }

  static async getDraft(jobId: string): Promise<ApplicationDraft | null> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<ApplicationDraft | null>>(
      API_ENDPOINTS.APPLICATIONS.DRAFT(jobId)
    );
    return response.data.data ?? null;
  }

  static async saveDraft(jobId: string, data: SubmitApplicationRequest): Promise<void> {
    const client = getApiClient();
    await client.put(API_ENDPOINTS.APPLICATIONS.DRAFT(jobId), data);
  }

  static async deleteDraft(jobId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.APPLICATIONS.DRAFT(jobId));
  }

  static async getMyApplications(
    params?: ApplicationListParams
  ): Promise<PaginatedResponse<Application>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.APPLICATIONS.MY_LIST, { params });
    return response.data.data;
  }

  static async getMyApplication(id: string): Promise<MyApplicationDetail> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<MyApplicationDetail>>(
      API_ENDPOINTS.APPLICATIONS.MY_GET(id)
    );
    return response.data.data;
  }

  static async getMyNotes(id: string): Promise<ApplicationNote[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<ApplicationNote[]>>(
      API_ENDPOINTS.APPLICATIONS.MY_NOTES(id)
    );
    return response.data.data ?? [];
  }

  static async withdrawApplication(id: string, reason?: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.APPLICATIONS.WITHDRAW(id), { reason });
  }

  static async revokeResumeAccess(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.APPLICATIONS.REVOKE_RESUME(id));
  }

  static async getOrgApplications(
    orgId: string,
    params?: OrgApplicationParams
  ): Promise<PaginatedResponse<Application>> {
    const client = getApiClient();
    const response = await client.get(API_ENDPOINTS.APPLICATIONS.ORG_LIST(orgId), { params });
    return response.data.data;
  }

  static async getPipelineSummary(orgId: string, jobId?: string): Promise<PipelineSummary> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<PipelineSummary>>(
      API_ENDPOINTS.APPLICATIONS.ORG_SUMMARY(orgId),
      { params: jobId ? { jobId } : undefined }
    );
    return response.data.data;
  }

  static async getOrgApplication<T = Application>(orgId: string, id: string): Promise<T> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<T>>(
      API_ENDPOINTS.APPLICATIONS.ORG_GET(orgId, id)
    );
    return response.data.data;
  }

  static async updateApplicationStatus(
    orgId: string,
    id: string,
    status: ApplicationStatus,
    message?: string,
    notifyCandidate = true
  ): Promise<Application> {
    const client = getApiClient();
    const response = await client.patch<ApiResponse<Application>>(
      API_ENDPOINTS.APPLICATIONS.ORG_UPDATE_STATUS(orgId, id),
      { status, message: message || undefined, notifyCandidate }
    );
    return response.data.data;
  }

  static async moveApplicationStage(
    orgId: string,
    id: string,
    stageId: string,
    notes?: string
  ): Promise<{ status: ApplicationStatus; stageId: string }> {
    const client = getApiClient();
    const response = await client.patch<
      ApiResponse<{ status: ApplicationStatus; stageId: string }>
    >(API_ENDPOINTS.APPLICATIONS.ORG_MOVE_STAGE(orgId, id), { stageId, notes });
    return response.data.data;
  }

  static async sendOffer(orgId: string, id: string, data: SendOfferRequest): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.APPLICATIONS.ORG_OFFER(orgId, id), data);
  }

  /** The resume the candidate applied with, rendered as an HTML page (for a sandboxed iframe). */
  static async getResumeHtml(orgId: string, id: string): Promise<string> {
    const client = getApiClient();
    const response = await client.get<string>(API_ENDPOINTS.APPLICATIONS.ORG_RESUME(orgId, id), {
      responseType: 'text',
    });
    return response.data;
  }

  static async downloadResumePdf(orgId: string, id: string): Promise<Blob> {
    const client = getApiClient();
    const response = await client.get<Blob>(API_ENDPOINTS.APPLICATIONS.ORG_RESUME(orgId, id), {
      params: { format: 'pdf' },
      responseType: 'blob',
    });
    return response.data;
  }

  /** Opens (or joins) the conversation with the candidate; returns its id. */
  static async openConversation(orgId: string, id: string): Promise<{ id: string }> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<{ id: string }>>(
      API_ENDPOINTS.APPLICATIONS.ORG_CONVERSATION(orgId, id)
    );
    return response.data.data;
  }

  static async addNote(
    orgId: string,
    applicationId: string,
    content: string,
    isPrivate = true
  ): Promise<void> {
    const client = getApiClient();
    await client.post(API_ENDPOINTS.APPLICATIONS.ORG_ADD_NOTE(orgId, applicationId), {
      content,
      isPrivate,
    });
  }

  static async getOrgNotes(orgId: string, applicationId: string): Promise<ApplicationNote[]> {
    const client = getApiClient();
    const response = await client.get<ApiResponse<ApplicationNote[]>>(
      API_ENDPOINTS.APPLICATIONS.ORG_GET_NOTES(orgId, applicationId)
    );
    return response.data.data ?? (response.data as unknown as ApplicationNote[]);
  }

  static async bulkAction(orgId: string, data: BulkActionRequest): Promise<BulkActionResult> {
    const client = getApiClient();
    const response = await client.post<ApiResponse<BulkActionResult>>(
      API_ENDPOINTS.APPLICATIONS.ORG_BULK(orgId),
      data
    );
    return response.data.data;
  }
}
